import {
  IonAlert,
  IonButton,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonMenuButton,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToolbar,
  useIonViewWillEnter,
} from "@ionic/react";
import { copyDomain, deleteDomain, getDomains } from "misc/phqHTTP";
import React, { useContext, useState } from "react";
import { DomainCopyRequest, PHQDomain } from "models/domain.model";
import { addCircle, copyOutline, trashBin, warningOutline } from "ionicons/icons";
import AddEditDomain from "./AddEditDomain";
import Footer from "layout/footer";
import { AppContext } from "context/app.context";

const DomainManagement = () => {
  const { notifySuccess, setIsLoading } = useContext(AppContext);
  const [domains, setDomains] = useState<PHQDomain[]>([]);
  const [status, setShowActive] = useState("active");
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("none");
  const [selectedDomain, setSelectedDomain] = useState<PHQDomain | undefined>();
  const [showCopyDomainAlert, setCopyDomainAlert] = useState(false);

  type Mode = "add" | "edit" | "none";

  const showModal = (mode: Mode) => {
    setMode(mode);
    setIsOpen(true);
  };

  const onAddDomain = (newDomain: PHQDomain) => {
    const data = domains
      .concat(newDomain)
      .sort((a: PHQDomain, b: PHQDomain) => a.Name.localeCompare(b.Name));
    setDomains(data);
  };

  const onEditDomain = (updatedDomain: PHQDomain) => {
    const idx = domains.findIndex((d) => d.DomainID === updatedDomain.DomainID);
    domains[idx] = updatedDomain;
    setDomains(domains);
  };

  const onSelectDomain = (d: PHQDomain) => {
    setSelectedDomain(d);
    showModal("edit");
  };

  const onCopyDomain = (e:any,d:PHQDomain) => {
    e.stopPropagation();
    setSelectedDomain(d);
    setCopyDomainAlert(true);
  }

  const onDelete = (id: number) => {
    deleteDomain(id).then(_ => {
      const updatedDomains = domains.filter(d => d.DomainID !== id);
      setDomains(updatedDomains);
      notifySuccess("Domain removed");
    })
  }

  const fetchDomains = () => {
    getDomains().then((res) => {
      setDomains(res.data.result);
    });
  }

  useIonViewWillEnter(() => {
    fetchDomains();
  });

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="primary" className="ion-no-border">
          <IonButton fill="clear" slot="start">
            <IonMenuButton menu="adminMenu" color="light"></IonMenuButton>
          </IonButton>
          <IonTitle className="ion-text-center">DOMAINS</IonTitle>
          <IonButton slot="end" onClick={(_) => showModal("add")}>
            <IonIcon slot="icon-only" size="large" icon={addCircle}></IonIcon>
          </IonButton>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonSegment
          onIonChange={(e) => setShowActive(e.detail.value!)}
          value={status}
        >
          <IonSegmentButton value="active">
            <IonLabel>Active</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="inactive">
            <IonLabel>Inactive</IonLabel>
          </IonSegmentButton>
        </IonSegment>

        <IonList>
          {domains
            .filter((d) => (status === "active" ? d.IsActive : !d.IsActive))
            .map((d) => (
              <IonItemSliding key={d.DomainID}>
                <IonItem 
                  title={!d.MessageTemplates?.length ? `No message templates associated with ${d.Name}`: d.Name} 
                  className="pointer" 
                  detail
                  onClick={(_) => onSelectDomain(d)}>
                    {d.Name}
                    {!d.MessageTemplates?.length && <IonIcon title="Message templates missing" color="warning" icon={warningOutline}></IonIcon>}
                  <IonButton slot="end" onClick={(e:any) => onCopyDomain(e,d)}>
                    <IonIcon className="mr-1" icon={copyOutline} /> Copy
                  </IonButton>
                </IonItem>
                <IonItemOptions>
                    <IonItemOption
                      color="danger"
                      onClick={() => onDelete(d.DomainID!)}>
                      <IonIcon icon={trashBin}></IonIcon>
                    </IonItemOption>
                 </IonItemOptions>
              </IonItemSliding>
            ))}
        </IonList>
        <AddEditDomain
          mode={mode}
          isOpen={isOpen}
          domain={mode === "edit" ? selectedDomain : undefined}
          setShowModal={setIsOpen}
          onEditDomain={onEditDomain}
          onAddDomain={onAddDomain}
        />
        <IonAlert
          isOpen={showCopyDomainAlert}
          onDidDismiss={() => setCopyDomainAlert(false)}
          header={'Confirm'}
          message={'Enter new domain name'}
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
              cssClass: 'secondary',
              handler: () => {
                setCopyDomainAlert(false);
              }
            },
            {
              text: 'Copy',
              handler: ({domainName}) => {
                const domainCopyReq = {
                  Id: selectedDomain?.DomainID,
                  Name: domainName
                } as DomainCopyRequest
                
                setIsLoading(true);
                copyDomain(domainCopyReq)
                .then(_ => {
                  setCopyDomainAlert(false);
                  notifySuccess("Domain copied!");
                  fetchDomains();
                })
                .finally(() => setIsLoading(false))
              }
            }
          ]}
          inputs={[
            {
              name: 'domainName',
              type: 'text',
              value: `Copy - ${selectedDomain?.Name}`
            },
          ]}
        />
      </IonContent>
      <Footer />
    </IonPage>
  );
};

export default DomainManagement;
